// Reporting chart preferences — PURE (persist/restore the chart section's
// per-user choices via the generic User.gridPreferences mechanism, gridKey
// 'reporting-charts'; same encode-as-tokens device as explorerPrefs.ts).
//
// Encoded as tokens in a single reserved preset's visibleColumns array:
//   chart:<metricKey>        — a selected metric card (in catalogue order)
//   group:<daily|weekly|monthly>
//   min:1                    — the big chart is minimised
//   multi:<1|0>              — the multi-metric trend chart is shown (default 1)
//   ccy:<code|native>        — the agency display currency (default native)
//   series:<metricKey>       — one series on that chart (in catalogue order)
//   style:<metricKey>:<bar|line|area>  — a per-metric style override (bar omitted)
//   wgt:<id>:<vizType>:<metricKey>:<flags>  — one user-added widget instance
//   can:<kind>:<id>:<x>:<y>:<w>:<h>         — ONE panel's place on the canvas
//                                             (lg breakpoint only)
// Reuses PATCH /api/me/preferences with no server change. Legacy / malformed
// prefs fall back to the defaults — never throws.
//
// ⚠️ THE `lay:tile:` / `lay:chart:` / `lay:widget:` TOKENS OF THE PREVIOUS BUILD
// ARE GONE. The top section used to be four separate grids, each with its own
// coordinate space; it is now ONE canvas, so those coordinates mean nothing and
// a stored arrangement resets once to the unified default. See chartLayout.ts.

import type { GridPreferences } from '../../types/user';
import {
  canvasPanelKey,
  decodeCanvasLayout,
  encodeCanvasLayout,
  type ChartPanelLayout,
} from '../../lib/reporting/chartLayout';
import {
  decodeWidgets,
  encodeWidgets,
  type ReportingWidget,
} from '../../lib/reporting/widgets';
import { DEFAULT_TREND_GROUPING, type TrendGrouping } from './chartDate';
import { CHART_METRICS, DEFAULT_CHART_METRIC_KEYS, isTrendMetricKey } from './trendSeries';
import { isMetaWidgetDimension } from './metaWidgetDimensions';
import {
  NATIVE_DISPLAY_CURRENCY,
  isNativeDisplay,
} from '../../lib/reporting/currencyConversion';

export const REPORTING_CHARTS_GRID_KEY = 'reporting-charts';

/**
 * The CHART panels this dashboard can put on the canvas: the selected-metric
 * trend, and the multi-metric trend (several metrics on one canvas — the
 * capability the single-metric chart deliberately does not give).
 */
export const REPORTING_CHART_PANEL_KEYS = ['trend', 'multiTrend'] as const;

/**
 * How many series the multi-metric chart will draw at once.
 *
 * Past four the lines stop being comparable — different metrics live at wildly
 * different magnitudes on one shared axis, so a fifth line is usually a flat
 * trace along the floor. Same cap as the Google chart's picker.
 */
export const MAX_MULTI_TREND_SERIES = 4;

/** What the multi-metric chart starts on: the two headline metrics. */
export const DEFAULT_MULTI_TREND_METRICS: string[] = ['spend', 'leads'];

/**
 * Every canvas panel key this dashboard can produce — the union of its tiles,
 * its charts and the user's current widgets, each KIND-QUALIFIED.
 *
 * ⚠️ THE UNION IS THE PRUNING CATALOGUE, and it must include panels that are
 * currently HIDDEN (an unselected metric, a chart toggled off) — that is what
 * lets a panel come back to where the user left it rather than being dumped at
 * the bottom. Only keys OUTSIDE it are pruned, so a retired panel or a deleted
 * widget can't leave a `can:` token accumulating forever.
 *
 * ⚠️ Kind-qualified because `trend` is both this dashboard's chart key and a
 * plausible metric key — bare, the chart and a tile would be the same panel.
 */
export function reportingCanvasPanelKeys(widgets: readonly ReportingWidget[]): string[] {
  return [
    ...CHART_METRICS.map((m) => canvasPanelKey('tile', m.key)),
    ...REPORTING_CHART_PANEL_KEYS.map((k) => canvasPanelKey('chart', k)),
    ...widgets.map((w) => canvasPanelKey('widget', w.id)),
  ];
}

// The reserved preset id we stash chart state under (never 'default').
const STATE_PRESET_ID = 'state';

export type ChartStyle = 'bar' | 'line' | 'area';
export const CHART_STYLES: ChartStyle[] = ['bar', 'line', 'area'];
export const DEFAULT_CHART_STYLE: ChartStyle = 'bar';

export interface ChartsState {
  selected: string[]; // metric cards to show (catalogue order)
  grouping: TrendGrouping;
  minimised: boolean; // big chart collapsed
  styles: Record<string, ChartStyle>; // per-metric style; absent = bar
  /**
   * Whether the multi-metric trend chart is on the canvas. Default ON, matching
   * how the Google dashboard exposes the same chart.
   *
   * ⚠️ It is stored EXPLICITLY (`multi:1` / `multi:0`) rather than by presence.
   * Absence has to mean "a legacy pref, use the default" — so with a
   * presence-only token, turning the chart OFF would be indistinguishable from
   * never having chosen, and it would silently come back on the next load. Same
   * reason the Google catalogue needs its `charts:none` marker.
   */
  multiTrend: boolean;
  /** Which metrics that chart plots, in catalogue order, capped. */
  trendMetrics: string[];
  /**
   * The agency DISPLAY CURRENCY — 'native' (the default, per-currency exactly as
   * before) or a currency code every money figure is converted into.
   *
   * ⚠️ Stored as-typed and NOT validated against the rate table here: this
   * decoder is pure and has no snapshot to check against. Resolution happens at
   * USE time via `normalizeDisplayCurrency`, which falls back to Native for a
   * code the current table can't reach — so a currency dropped from the table
   * degrades to Native rather than to some other currency's figures.
   */
  displayCurrency: string;
  /**
   * The user-added widgets, in render order. EMPTY is the fresh-user state AND
   * what a legacy pref decodes to — the widget region shows its "Add widget"
   * prompt, which is exactly right for both.
   *
   * ⚠️ Unlike `selected`, empty never falls back to a default set. A widget the
   * user did not ask for is not a sensible default; an empty KPI row is.
   */
  widgets: ReportingWidget[];
  /**
   * The ONE canvas arrangement — tiles, charts and widgets together, each panel
   * kind-qualified. EMPTY means "use the default arrangement", which is what a
   * legacy pref decodes to AND what "Reset layout" saves, so there is a single
   * path back to the default.
   */
  canvasLayout: ChartPanelLayout[];
}

const VALID_GROUPINGS: TrendGrouping[] = ['daily', 'weekly', 'monthly'];
const isStyle = (s: string): s is ChartStyle => (CHART_STYLES as string[]).includes(s);

export function defaultChartsState(): ChartsState {
  return {
    selected: [...DEFAULT_CHART_METRIC_KEYS],
    grouping: DEFAULT_TREND_GROUPING,
    minimised: false,
    styles: {},
    multiTrend: true,
    trendMetrics: [...DEFAULT_MULTI_TREND_METRICS],
    displayCurrency: NATIVE_DISPLAY_CURRENCY,
    widgets: [],
    canvasLayout: [],
  };
}

/**
 * Keep only valid metric keys, in catalogue order, de-duplicated, capped.
 *
 * Empty → the default pair. Unlike the widgets (where empty is a legitimate "I
 * have none"), a chart with no series is a titled empty box — it reads as broken
 * rather than as a choice, which is what the chart's own on/off tick is for.
 */
export function sanitizeTrendMetrics(keys: readonly string[]): string[] {
  const wanted = new Set(keys.filter(isTrendMetricKey));
  const ordered = CHART_METRICS.filter((m) => wanted.has(m.key))
    .map((m) => m.key)
    .slice(0, MAX_MULTI_TREND_SERIES);
  return ordered.length > 0 ? ordered : [...DEFAULT_MULTI_TREND_METRICS];
}

// Keep only valid metric keys, in catalogue order, de-duplicated. Empty → default
// set (a cleared selection or legacy pref never yields zero cards).
export function sanitizeSelected(keys: readonly string[]): string[] {
  const wanted = new Set(keys.filter(isTrendMetricKey));
  const ordered = CHART_METRICS.filter((m) => wanted.has(m.key)).map((m) => m.key);
  return ordered.length > 0 ? ordered : [...DEFAULT_CHART_METRIC_KEYS];
}

// Resolve a metric's style (bar default; unknown value ignored).
export function styleForMetric(styles: Record<string, ChartStyle>, key: string): ChartStyle {
  const s = styles[key];
  return s && isStyle(s) ? s : DEFAULT_CHART_STYLE;
}

export function encodeChartsState(state: ChartsState): GridPreferences {
  const selected = sanitizeSelected(state.selected);
  const grouping: TrendGrouping = VALID_GROUPINGS.includes(state.grouping)
    ? state.grouping
    : DEFAULT_TREND_GROUPING;
  const tokens = [
    ...selected.map((k) => `chart:${k}`),
    `group:${grouping}`,
    ...(state.minimised ? ['min:1'] : []),
    // Explicit both ways — see the ChartsState note for why absence can't mean
    // "off" here.
    `multi:${state.multiTrend ? '1' : '0'}`,
    ...sanitizeTrendMetrics(state.trendMetrics).map((k) => `series:${k}`),
    // Native is the DEFAULT, so it needs no token — absence and 'native' mean
    // the same thing here (unlike `multi:`, where "off" had to be told apart
    // from "never chose"). Only a real currency is written.
    ...(isNativeDisplay(state.displayCurrency) ? [] : [`ccy:${state.displayCurrency}`]),
    // Only persist non-default (non-bar) styles for selectable metrics.
    ...CHART_METRICS.filter((m) => {
      const s = state.styles[m.key];
      return s && isStyle(s) && s !== DEFAULT_CHART_STYLE;
    }).map((m) => `style:${m.key}:${state.styles[m.key]}`),
    // The user-added widgets. `encodeWidgets` refuses anything it could not read
    // back, so a widget can never be written in a shape that then decodes away.
    ...encodeWidgets(state.widgets),
    // The canvas, pruned to the union catalogue on the way out — a retired
    // panel, a dropped metric or a removed widget can't leave a token living
    // forever, and a removed widget's placement goes in the SAME save.
    ...encodeCanvasLayout(
      state.canvasLayout.filter((i) =>
        reportingCanvasPanelKeys(state.widgets).includes(i.key),
      ),
    ),
  ];
  return {
    activePresetId: STATE_PRESET_ID,
    presets: [{ id: STATE_PRESET_ID, name: 'Chart state', visibleColumns: tokens }],
  };
}

export function decodeChartsState(prefs: GridPreferences | undefined): ChartsState {
  const preset = prefs?.presets.find((p) => p.id === STATE_PRESET_ID);
  if (!preset) return defaultChartsState();
  const tokens = preset.visibleColumns;

  const selected = sanitizeSelected(
    tokens.filter((t) => t.startsWith('chart:')).map((t) => t.slice('chart:'.length)),
  );

  const groupToken = tokens.find((t) => t.startsWith('group:'))?.slice('group:'.length);
  const grouping: TrendGrouping =
    groupToken && (VALID_GROUPINGS as string[]).includes(groupToken)
      ? (groupToken as TrendGrouping)
      : DEFAULT_TREND_GROUPING;

  const minimised = tokens.includes('min:1');

  const displayCurrency = decodeCurrencyToken(
    tokens.find((t) => t.startsWith('ccy:'))?.slice('ccy:'.length),
  );

  // Absent (a legacy pref) → the default, ON. Only an explicit `multi:0` hides it.
  const multiTrend = !tokens.includes('multi:0');
  const trendMetrics = sanitizeTrendMetrics(
    tokens.filter((t) => t.startsWith('series:')).map((t) => t.slice('series:'.length)),
  );

  const styles: Record<string, ChartStyle> = {};
  for (const t of tokens) {
    if (!t.startsWith('style:')) continue;
    const rest = t.slice('style:'.length);
    const sep = rest.lastIndexOf(':');
    if (sep <= 0) continue;
    const key = rest.slice(0, sep);
    const style = rest.slice(sep + 1);
    if (isTrendMetricKey(key) && isStyle(style)) styles[key] = style;
  }

  return {
    selected,
    grouping,
    minimised,
    styles,
    multiTrend,
    trendMetrics,
    displayCurrency,
    // ⚠️ `isTrendMetricKey` is THIS dashboard's catalogue — a widget token whose
    // metric Meta doesn't have (a Google key, or one retired from CHART_METRICS)
    // is dropped rather than defaulted onto some other metric.
    widgets: decodeWidgets(tokens, isTrendMetricKey, isMetaWidgetDimension),
    canvasLayout: decodeCanvasLayout(tokens),
  };
}

/**
 * A stored `ccy:` value → a display-currency string.
 *
 * Shape-only: a plausible ISO-ish code passes, anything else becomes Native. The
 * table-membership check cannot happen here (no snapshot), and deliberately
 * shouldn't — a user whose currency is temporarily missing from the table should
 * get it back when the table does, not have their preference erased on one load.
 */
export function decodeCurrencyToken(raw: string | undefined): string {
  if (!raw || isNativeDisplay(raw)) return NATIVE_DISPLAY_CURRENCY;
  return /^[A-Za-z]{3,8}$/.test(raw) ? raw.toUpperCase() : NATIVE_DISPLAY_CURRENCY;
}
